/* eslint-disable max-lines -- SILENCED_API_ERRORS is one declarative allowlist; splitting it would scatter the policy */
import { NoddiAsyncError } from 'noddi-types';
import type { SeverityLevel } from '../utils/logUtil';

/**
 * Severity for a failed API request. Downgrades to `debug` when:
 *  - status is undefined (no response — offline, cancelled, extension-blocked)
 *  - status is 401 (unauthenticated — handled per-app: web's AuthProvider
 *    redirects to login, mobile's handleNoddiAsyncError calls logout())
 *  - status is 429 (global rate limit)
 *  - the (url, code/status) pair is in SILENCED_API_ERRORS
 *
 * `debug` is swallowed by `logUtil` and never reaches Sentry.
 */
export const getApiErrorSeverity = (error: NoddiAsyncError): SeverityLevel => {
  if (error.status === undefined) {
    return 'debug';
  }
  if (error.status === 401) {
    return 'debug';
  }
  // Rate limits are expected (global 100/min). Frontend should retry, not page Sentry.
  if (error.status === 429) {
    return 'debug';
  }
  // Server failures remain actionable even when their payload reuses an expected refusal code.
  if (error.status >= 500) {
    return 'error';
  }
  if (isSilencedApiError(error)) {
    return 'debug';
  }
  return 'error';
};

type SilencedApiError = (
  | { urlContains: string; urlPathEndsWith?: string }
  | { urlContains?: never; urlPathEndsWith: string }
) & {
  /** Restrict to these HTTP methods (lowercase); omitted = any method. */
  methods?: readonly ('get' | 'post' | 'patch' | 'put' | 'delete')[];
  errorCodes?: readonly string[];
  statuses?: readonly number[];
};

const isSilencedApiError = (error: NoddiAsyncError): boolean => {
  const url = error.config?.url;
  if (!url) {
    return false;
  }
  return SILENCED_API_ERRORS.some((entry) => {
    const urlPath = url.split('?')[0] ?? url;
    const urlMatches =
      (entry.urlContains === undefined || urlPath.includes(entry.urlContains)) &&
      (entry.urlPathEndsWith === undefined || urlPath.endsWith(entry.urlPathEndsWith));
    if (!urlMatches) {
      return false;
    }
    if (entry.methods && !entry.methods.includes((error.config?.method ?? '').toLowerCase() as never)) {
      return false;
    }
    if (entry.statuses?.includes(error.status ?? -1)) {
      return true;
    }
    return (
      entry.errorCodes?.some((code) => error.response?.data.errors?.some((apiError) => apiError.code === code)) ?? false
    );
  });
};

/**
 * (URL substring → error codes / statuses) pairs to downgrade from `error`
 * to `debug` in Sentry. Use for expected user-input or permission failures
 * where alert noise outweighs signal. Debug messages are swallowed by
 * default, not sent to Sentry, but can be viewed in the console.
 */
const SILENCED_API_ERRORS: SilencedApiError[] = [
  {
    // An order edit draft is validated by its preview; the dialog shows each refusal inline.
    urlContains: '/order-edit/',
    methods: ['post'],
    statuses: [400]
  },
  {
    // Mutation toasts retain these typed refusals; stale choices and invalid input are expected.
    urlContains: '/route-solutions/',
    urlPathEndsWith: '/select/',
    methods: ['post'],
    errorCodes: ['route_solution_already_selected']
  },
  {
    urlContains: '/route-items/',
    urlPathEndsWith: '/send-next-arrival-sms/',
    methods: ['post'],
    errorCodes: ['duplicate_sms_identical_content']
  },
  {
    urlContains: '/routes/',
    urlPathEndsWith: '/add-booking/',
    methods: ['patch'],
    errorCodes: ['invalid_route_position']
  },
  {
    urlContains: '/fleet-services/schedules/',
    urlPathEndsWith: '/vehicles/',
    methods: ['post'],
    errorCodes: ['fleet_schedule_vehicle_already_enrolled']
  },
  {
    urlPathEndsWith: '/capacity-contributions/baseline/',
    methods: ['post'],
    errorCodes: ['capacity_baseline_stack_overlap_api_error']
  },
  {
    // A worker without capabilities cannot offer time; ops sets them up, the worker app toasts the refusal.
    urlPathEndsWith: '/capacity-contributions/',
    methods: ['post'],
    errorCodes: ['service_worker_does_not_have_any_capabilities']
  },
  {
    urlContains: 'addresses/create-from-google-place-id/',
    errorCodes: ['incomplete_address']
  },
  {
    urlContains: 'booking-proposals/',
    statuses: [404, 410],
    errorCodes: [
      'booking_proposal_closed_by_staff',
      'booking_proposal_rejected',
      'booking_proposal_rejected_by_customer',
      'booking_proposal_closed',
      'booking_proposal_is_used_has_booking',
      'booking_proposal_expired',
      'booking_proposal_not_published',
      'booking_proposal_booked_outside_of_recall',
      'not_found'
    ]
  },
  {
    // Superuser-only badge list. Partner home fetches it for everyone; 403 is expected.
    urlContains: 'orders/refundable/',
    statuses: [403]
  },
  {
    urlContains: 'sync-jobs/run/',
    errorCodes: ['request_throttled', 'throttled']
  },
  {
    urlContains: 'users/verify-phone-number/',
    errorCodes: ['invalid_verification_code']
  },
  {
    // The five-minute challenge runs out while the customer reads the account list.
    urlContains: 'users/select-account/',
    errorCodes: ['account_selection_expired', 'account_selection_invalid']
  },
  {
    // The ten-minute signup challenge runs out, or the network changes, while the customer fills in the form;
    // the form offers to verify the phone again.
    urlContains: 'users/signup-reconciliation/',
    errorCodes: ['signup_reconciliation_expired', 'signup_reconciliation_invalid']
  },
  {
    // The SMS provider can block a number, the number is a landline, or its country is outside the SMS allowlist;
    // the login form renders the typed refusal.
    urlContains: 'users/send-phone-number-verification/',
    errorCodes: ['twilio_phone_fraud_blocked', 'phone_number_cannot_receive_sms', 'phone_number_country_code_blocked']
  },
  {
    urlContains: 'memberships/add-naf-membership/',
    errorCodes: ['user_not_naf_member']
  },
  {
    // Scoped to `memberships/` so it does not also silence an identical
    // error code raised by `referrals/<id>/activate/`.
    urlContains: 'memberships/',
    errorCodes: ['invalid_code', 'already_member']
  },
  {
    urlContains: 'campaigns/',
    errorCodes: [
      'campaign_auth_code_invalid',
      'campaign_brand_mismatch',
      'campaign_expired',
      'campaign_no_longer_available',
      'campaign_requires_naf_verification'
    ]
  },
  {
    // Public campaign or referral link with a code no one ever used: the page
    // shows its not-found state.
    urlContains: 'campaigns/from-code/',
    methods: ['get'],
    statuses: [404]
  },
  {
    urlContains: 'referrals/from-slug/',
    methods: ['get'],
    statuses: [404]
  },
  {
    urlContains: 'referrals/',
    errorCodes: [
      'referral_brand_mismatch',
      'referral_cannot_activate_own',
      'referral_disabled',
      'referral_ineligible',
      'referral_no_longer_available'
    ]
  },
  {
    urlContains: 'dispatch/preview/',
    errorCodes: [
      'transfer_dispatch_storage_facility_missing_reload_node',
      'transfer_dispatch_storage_facility_missing_stop_timing',
      'transfer_dispatch_stop_timing_missing_wheel_cargo_config',
      'route_planner_invalid_problem'
    ]
  },
  {
    // A full fleet slot is a normal booking choice refusal shown by the form.
    urlContains: 'fleet-services/schedules/',
    errorCodes: ['fleet_schedule_slot_full']
  },
  {
    // Schedule id typed into the URL, or deleted while the tab was open. The
    // detail page renders its not-found state; three sibling reads (detail,
    // vehicles, availability) would otherwise report one mistyped id.
    urlContains: 'fleet-services/schedules/',
    methods: ['get'],
    statuses: [404]
  },
  {
    // Address + product category combination outside coverage — expected in
    // multi-address investigation tools and when customers explore edge areas.
    urlContains: 'delivery-windows/',
    errorCodes: ['service_department_not_found_api_error', 'address_not_close_to_service_department']
  },
  {
    // Customer/partner booking catalog probe: no products for address/brand/car
    // is a normal selection outcome (UI shows "No services available").
    urlContains: 'sales-items/initial-available-for-booking/',
    errorCodes: [
      'no_sales_items_for_brands_at_address',
      'no_sales_items_at_address',
      'no_sales_items_for_car_rules',
      'no_sales_items_upcoming_wheel_storage_booking',
      'no_sales_items_active_wheel_storage',
      'no_sales_items_wheel_storage_catalog_state',
      'no_sales_items_available_for_car',
      'no_service_departments_available_for_bookings'
    ]
  },
  {
    // Unknown plate — expected lookup miss (customer/partner search).
    urlContains: 'cars/from-license-plate',
    statuses: [404]
  },
  {
    // Worker scanner: a plate read off a label that no car has; the scanner drops it without a suggestion.
    urlPathEndsWith: '/cars/worker-overview/',
    methods: ['get'],
    statuses: [404]
  },
  {
    // Worker inspection form: no stored dimensions — UI falls back to manual input.
    urlContains: 'car-wheel-set-dimensions/',
    statuses: [404]
  },
  {
    // Bad credentials — expected auth failure. `v1/login/` does not match login-token/.
    urlContains: 'v1/login/',
    statuses: [400]
  },
  {
    // Facility/partner staff without this permission; list is still fetched.
    urlContains: 'user-groups/minimal/',
    statuses: [403]
  },
  {
    // Slot taken while booking — expected race, UI asks the user to pick another.
    // Legacy adapter booking path; station reserve is bookings/station/reserve-slot/.
    urlContains: 'eontyre/booking/',
    statuses: [409]
  },
  {
    // Slot taken during reservation — the picker asks the customer to choose again.
    urlPathEndsWith: '/bookings/station/reserve-slot/',
    methods: ['post'],
    errorCodes: ['workstation_slot_unavailable_api_error', 'eontyre_time_slot_unavailable_error']
  },
  {
    // Slot taken during final creation — UI shows the refusal and analytics retain the conversion failure.
    urlPathEndsWith: '/bookings/station/',
    methods: ['post'],
    errorCodes: ['workstation_slot_unavailable_api_error']
  },
  {
    // A worker without recorded PPE sizes is the card's empty state.
    urlContains: '/ppe-sizes/',
    methods: ['get'],
    errorCodes: ['equipment_ppe_size_not_found']
  },
  {
    // Most API keys have no OAuth client. The key page treats that 404 as "not enabled".
    urlContains: '/api-keys/',
    urlPathEndsWith: '/oauth-client/',
    methods: ['get'],
    statuses: [404],
    errorCodes: ['api_key_oauth_client_missing']
  },
  {
    // A correction can invalidate its source time log before the cache refreshes.
    urlContains: 'time-tracking/time-logs/',
    errorCodes: ['not_found']
  },
  {
    // Booking cannot be paid yet — customer sees the backend's actionable refusal.
    urlContains: '/payment-session/',
    errorCodes: ['booking_payment_after_completion_required']
  },
  {
    // The payment webhook confirmed the booking after the return page loaded; the page refetches and shows it paid.
    urlContains: '/payment-session/',
    methods: ['post'],
    errorCodes: ['booking_not_payable_for_payment_session']
  },
  {
    // Wrong current password or a new password the validators reject; the form renders the refusal.
    urlPathEndsWith: '/users/password/change/',
    methods: ['post'],
    errorCodes: ['user_current_password_invalid', 'password_same_as_current', 'password_too_weak', 'invalid']
  },
  {
    // A mistyped or unknown email; the reset form renders "no account".
    urlPathEndsWith: '/users/password-reset/',
    methods: ['post'],
    errorCodes: ['user_does_not_exist']
  },
  // A reused or weak new password, or an old or already-used link or code; the reset form renders the refusal.
  ...['/users/password-reset/confirm/', '/users/password-reset/confirm-otp/'].map((urlPathEndsWith) => ({
    urlPathEndsWith,
    methods: ['post'] as const,
    errorCodes: [
      'password_same_as_current',
      'password_too_weak',
      'password_reset_token_expired',
      'password_reset_token_invalid'
    ]
  })),
  {
    // An old or already-used sign-in link; the page asks for a new one.
    urlPathEndsWith: '/users/magic-link/confirm/',
    methods: ['post'],
    errorCodes: ['magic_link_token_expired', 'magic_link_token_invalid']
  },
  {
    // A car with wheels in storage cannot leave the customer; the toast names the blocker.
    urlContains: '/user-groups/',
    urlPathEndsWith: '/remove-cars/',
    methods: ['patch'],
    errorCodes: ['car_has_wheels_in_storage']
  },
  {
    // Transfer stop completed before its cargo is in place; the stop sheet shows the refusal.
    urlContains: '/route-items/',
    urlPathEndsWith: '/transfer/complete/',
    methods: ['post'],
    errorCodes: ['route_item_cargo_expectation_transfer_not_ready']
  },
  {
    // Customer not linked to the selected organization; the referrals tab shows its empty state.
    urlContains: '/user-groups/',
    urlPathEndsWith: '/referrals/',
    methods: ['get'],
    errorCodes: ['not_found']
  },
  {
    // Customer deleted after being picked; the selection lingers in sessionStorage.
    urlPathEndsWith: '/cars-for-tire-offer/',
    methods: ['get'],
    errorCodes: ['not_found']
  },
  {
    // Brand without referral settings; the panel shows its "not configured" state.
    urlContains: '/brands/',
    urlPathEndsWith: '/referral-settings/',
    methods: ['get'],
    errorCodes: ['referral_settings_not_found']
  },
  {
    // Wheel set without wheels cannot be stored; the 400 names the plate so staff fix the data.
    urlPathEndsWith: '/storage-object-groups/create-for-wheel-sets/bulk/',
    methods: ['post'],
    errorCodes: ['car_wheel_set_has_no_wheels']
  },
  {
    // Manual recall publish keeps the backend's actionable November 1 refusal in the modal.
    urlContains: '/publish-to-customer/',
    methods: ['post'],
    errorCodes: ['recall_publish_studded_winter_tires_too_early']
  },
  {
    // Double clock-in / overtime gate — UI already surfaces these as typed 400s.
    urlContains: 'time-tracking/me/clock-in/',
    errorCodes: ['time_log_already_open', 'overtime_acknowledgment_required', 'time_log_blocked_by_active_route']
  },
  {
    // A manager stopping a timer the worker already stopped, or at an impossible
    // time — the stop-timer dialog shows the backend's detail.
    urlContains: 'time-tracking/time-logs/',
    urlPathEndsWith: '/clock-out/',
    methods: ['post'],
    errorCodes: ['time_log_not_open', 'time_log_in_future', 'time_log_invalid_interval']
  },
  {
    // Completing a tread-depth task that is already gone — stale task id.
    urlContains: 'complete-car-tire-tread-depth-measurement/',
    statuses: [404]
  },
  {
    // Registration and account email forms render the typed duplicate-account refusal.
    // Create posts to /accounts/; contact updates still patch /users/{id}/.
    urlContains: '/accounts/',
    methods: ['post'],
    errorCodes: ['user_phone_or_email_already_exists']
  },
  {
    urlContains: '/users/',
    methods: ['post', 'patch'],
    errorCodes: ['user_phone_or_email_already_exists']
  },
  {
    // Someone else already stores wheels for that plate. A normal refusal on a
    // back-office booking; the mutation toast shows the backend's plate-specific
    // detail and the agent picks another car or clears the storage first.
    urlPathEndsWith: '/bookings/',
    methods: ['post'],
    errorCodes: ['wheel_storage_conflict', 'delivery_window_closed_for_new_bookings']
  },
  // Filter option lists a user is not allowed to read stay empty; the picker still works.
  // GET only — a forbidden mutation on these resources is still a real failure.
  { urlContains: '/storage-facilities/', methods: ['get'], statuses: [403] },
  { urlContains: '/membership-programs/', methods: ['get'], statuses: [403] },
  { urlContains: '/coupon-groups/', methods: ['get'], statuses: [403] },
  {
    // Brand logo lookup for a relationship badge — the name renders without it.
    urlContains: '/brands/',
    methods: ['get'],
    statuses: [403]
  },
  {
    // One booking cannot mix start/renew/end tire hotel products; the toast shows the backend's explanation.
    urlContains: '/add-extra-sales-items/',
    methods: ['post'],
    errorCodes: ['wheel_storage_commercial_family_conflict']
  }
];
